import React from 'react';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import ProductListContainer from 'src/components/BeaconV2Components/MetricList/ProductListContainer';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { Field } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy';
import { useAppSelector } from 'src/utils/Hooks';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import { SALES_TABLE_HEADER_DISPLAY_NAMES } from 'src/components/BeaconV2Components/ExperimentalLayout/Summary/Sales/constants';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

/**
 * Shows charts for the inventory summary page
 */

const fields: Field[] = [
  {
    name: 'unitsSold',
    displayName: SALES_TABLE_HEADER_DISPLAY_NAMES.UNITS_SOLD
  },
  {
    name: 'retailPrice',
    displayName: SALES_TABLE_HEADER_DISPLAY_NAMES.RETAIL_PRICE
  },
  {
    name: 'instockRate',
    displayName: SALES_TABLE_HEADER_DISPLAY_NAMES.INSTOCK_RATE
  },
  {
    name: 'unitsOnHand',
    displayName: SALES_TABLE_HEADER_DISPLAY_NAMES.UNITS_ON_HAND
  },

  {
    name: 'inventoryWeeksOnHand',
    displayName: SALES_TABLE_HEADER_DISPLAY_NAMES.INVENTORY_WEEKS_ON_HAND
  },
  {
    name: 'inventoryRetailValue',
    displayName: SALES_TABLE_HEADER_DISPLAY_NAMES.INVENTORY_RETAIL_VALUE
  }
];
const InventorySummaryPage = () => {
  const { endWeek: mainEndWeekId } = useAppSelector((state) => state.mainTimePeriod);
  const { endWeek: comparisonEndWeekId } = useAppSelector((state) => state.comparisonTimePeriod);
  const weekEndingFields = fields.map(removeComparisonFormatter);
  const { isProductEntity } = useProductEntity();

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_INSTOCK_RATE.name}
          chartName={CHART_NAMES.BEACON_INSTOCK_RATE_SPLINE}
        >
          <BeaconComparableSplineChart
            indexName="sales"
            fieldName="instockRate"
            aggregationFunctionForTotal="avg"
            beaconChartWithLegendOverride={(defaultProps, metricByWeekValues) => {
              return {
                ...defaultProps,
                primaryLegendProps: {
                  ...defaultProps.primaryLegendProps,
                  change: metricByWeekValues.formattedPercentChange,
                  direction: getDirection(metricByWeekValues.percentChange),
                  metric: metricByWeekValues.formattedPrimaryTotal
                },
                comparisonLegendProps: {
                  ...defaultProps.comparisonLegendProps,
                  metric: metricByWeekValues.formattedSecondaryTotal
                }
              };
            }}
          />
        </ChartContextProvider>
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_UNITS_ON_HAND.name}
          chartName={CHART_NAMES.BEACON_UNITS_ON_HAND_SPLINE}
        >
          <BeaconComparableSplineChart
            indexName="sales"
            fieldName="unitsOnHand"
            aggregationFunctionForTotal="cardinality"
          />
        </ChartContextProvider>
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_WEEKS_ON_HAND.name}
          chartName={CHART_NAMES.BEACON_WEEKS_ON_HAND_SPLINE}
        >
          <BeaconComparableSplineChart
            indexName="sales"
            fieldName="inventoryWeeksOnHand"
            title="Weeks on Hand - Historical Run Rate"
          />
        </ChartContextProvider>
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_INVENTORY_RETAIL_VALUE.name}
          chartName={CHART_NAMES.BEACON_INVENTORY_RETAIL_VALUE_SPLINE}
        >
          <BeaconComparableSplineChart
            indexName="sales"
            fieldName="inventoryRetailValue"
            aggregationFunctionForTotal="cardinality"
          />
        </ChartContextProvider>

        {isProductEntity ? (
          <MetricListContainer
            entityTableHeaderProps={{
              title: 'Units on Hand by Week'
            }}
            indexName="sales"
            sortFieldName="unitsOnHand"
            fields={weekEndingFields}
            headColumnDefinition={getWeekColumnDefinition({
              getTableColumnProps(defaults) {
                return {
                  ...defaults,
                  style: {
                    ...defaults.style,
                    width: '220px'
                  }
                };
              }
            })}
            postProcessData={(data) => {
              return data.sort((a, b) => b.weekId.localeCompare(a.weekId));
            }}
            useTableDataProps={{
              groupByFieldName: 'weekId',
              filterResponse: () => true,
              itemsPerPage: 100
            }}
            showGrid={false}
          />
        ) : (
          <ProductListContainer
            indexName="sales"
            fields={fields}
            sortFieldName="unitsOnHand"
            entityTableHeaderProps={{ title: 'Units on Hand by Product' }}
            useTableDataProps={{
              customComparisonStartWeekId: comparisonEndWeekId,
              customComparisonEndWeekId: comparisonEndWeekId,
              buildAggregation: (aggBuilder) =>
                aggBuilder
                  .clearConditionRangeFilters()
                  .addConditionRangeFilter('weekId', mainEndWeekId, mainEndWeekId)
                  .replaceComparisonRangeFilter('weekId', comparisonEndWeekId, comparisonEndWeekId),
              buildRequest: (builder) =>
                builder.removeConditionRangeFilters((rangeFilter) => rangeFilter.fieldName === 'weekId')
            }}
          />
        )}
      </Flex>
    </BasePageContainer>
  );
};

export default InventorySummaryPage;
